/**
 * @module app.core
 */

import { autoinject, transient } from 'aurelia-framework';
import { ApiModelIdentifier } from 'app/shared/models/essette-api';
import { StandardLoadOptions } from 'app/shared/lib/essette-site';
import { ICorrespondenceConfig } from 'app/core/models/essette-interfaces';
import { ApiPostResponse, ApiQueryResponse, Api, ApiQuery, ApiAction } from 'app/shared/lib/essette-api';
import * as Core from 'app/core/models/essette-api';

const defaultConfig: ICorrespondenceConfig = {
    transitionalPageId: '',
    serviceRootUrl: '',
    targetColumnHeader: '',
    targetColumnProperty: ''
}

@transient()
@autoinject()
export class CorrespondenceService {

    private isConfigured: boolean = false;
    private config: ICorrespondenceConfig;

    constructor(protected api: Api) {
        this.config = defaultConfig;
    }

    public configure(config: ICorrespondenceConfig): void {
        this.config = Object.assign({}, defaultConfig, config); // Clones the incoming config object parameter, using defaultConfig as fallback for any missing properties, and assigns it to this.config
        this.isConfigured = true;
    }

    public async loadLettersByTarget(targetId: ApiModelIdentifier, isClosed: boolean, options?: StandardLoadOptions, includes: string[] = [], filterPortal: boolean = false, fields: string[] = []): Promise<ApiQueryResponse<Core.CorrespondenceLetter>> {
        this.checkConfiguration();

        const query = new ApiQuery(`${this.config.serviceRootUrl}/${targetId}/correspondence/letters`)
            .applyStandardLoadOptions(options)
            .filterBoolean('status.flag.isclosed', isClosed)
            .include(...includes)
            .field(...fields);

        if(filterPortal) {
            query.filterBoolean('isActive', true)
                .filterBoolean('status.flag.isSuccessful', true)
                .filterBoolean('status.flag.triggersCopies', false);
        }

        if(this.config.targetInclude) {
            this.config.targetInclude.forEach(i => { query.include(i); })
        }

        return await this.api.get(query);
    }

    public async loadLettersByMember(memberId: string, isClosed?: boolean, options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<ApiQueryResponse<Core.CorrespondenceLetter>> {
        this.checkConfiguration();
        const query = new ApiQuery(`${this.config.rollupRootUrl}/letters`)
            .filterString('member.id', memberId)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(isClosed != undefined) {
            query.filterBoolean('status.flag.isclosed', isClosed);
        }

        if(this.config.targetInclude) {
            query.include(...this.config.targetInclude);
        }

        if(this.config.targetFields) {
            query.field(...this.config.targetFields);
        }

        return await this.api.get(query);
    }

    public async loadLetterByTarget(letterId: string, targetId: ApiModelIdentifier, options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<ApiQueryResponse<Core.CorrespondenceLetter>> {
        this.checkConfiguration();

        const query = new ApiQuery(`${this.config.serviceRootUrl}/${targetId}/correspondence/letters/${letterId}`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadLetter(letterId: string, options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<ApiQueryResponse<Core.CorrespondenceLetter>> {
        this.checkConfiguration();

        const query = new ApiQuery(`${this.config.rollupRootUrl}/letters/${letterId}`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadTemplates(options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<ApiQueryResponse<Core.CorrespondenceTemplate>> {
        this.checkConfiguration();
        const query = new ApiQuery(`${this.config.serviceRootUrl}/correspondence/templates`)
            .applyStandardLoadOptions(options)
            .filterBoolean('flag.allowAdHoc', true)
            .filterBoolean('isPublished', true)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadTemplateCopiesByTemplateId(templateId: string, options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<ApiQueryResponse<Core.CorrespondenceTemplateCopy>> {
        const query = new ApiQuery(`${this.config.serviceRootUrl}/correspondence/templates/${templateId}/copies`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadStatuses(options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<ApiQueryResponse<Core.CorrespondenceLetterStatus>> {
        this.checkConfiguration();
        const query = new ApiQuery(`correspondence/letters/statuses`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async insertLetter(letter: Core.CorrespondenceLetter): Promise<ApiPostResponse> {
        this.checkConfiguration();

        const action = new ApiAction('insert', `${this.config.serviceRootUrl}/correspondence/letters`, letter);
        return await this.api.post(action);
    }

    public async cancelLetter(letter: Core.CorrespondenceLetter): Promise<ApiPostResponse> {
        this.checkConfiguration();

        const action = new ApiAction('cancel', `${this.config.serviceRootUrl}/correspondence/letters/${letter.id}`, letter);
        return await this.api.post(action);
    }

    public async resendLetterCopy(copy: Core.CorrespondenceLetterCopy): Promise<ApiPostResponse> {
        this.checkConfiguration();
        const action = new ApiAction('resend', `${this.config.serviceRootUrl}/correspondence/letters/copies/${copy.id}`, copy);
        return await this.api.post(action);
    }

    private checkConfiguration(): void {
        if(!this.isConfigured) {
            throw Error('Correspondence service requires module-specific configuration! Use configure() method.');
        }
    }

    public async downloadLetterCopy(letterCopyId: string): Promise<Blob> {
        this.checkConfiguration();
        const request = this.api
            .download(`${this.config.serviceRootUrl}/correspondence/letters/copies/${letterCopyId}`);
        return await this.api.get(request);
    }

}
